<template>
  <v-container>
    <Announcement />

    <!-- Path Overview -->
    <v-breadcrumbs
      class="pl-1 py-2"
      divider=">"
      :items="breadcrumb"
    ></v-breadcrumbs>
    <v-row no-gutters align="center" style="height: 40px">
      <!-- Back Button -->
      <v-btn text small class="px-1" @click="$router.go(-1)"
        ><v-icon left small> mdi-chevron-left </v-icon
        >{{ $langs.get("gen.goBack") }}</v-btn
      >
      <div class="ml-auto">
        <!-- Report Button -->
        <v-btn
          v-if="showReportBtn"
          small
          text
          :ripple="false"
          @click="reportProduct"
          ><v-icon small left>flag</v-icon>{{ $langs.get("gen.report") }}
        </v-btn>
        <!-- Share Button -->
        <v-btn small outlined class="ml-1" :ripple="false" @click="shareLink">
          <v-icon small left>mdi-share-variant</v-icon>
          Share
        </v-btn>
      </div>
    </v-row>

    <v-row v-if="product.title" class="my-0" justify="center">
      <!-- Images Viewer -->
      <v-col md="7" cols="12">
        <ImagesPreview height="310" :imagesURL="product.images" />
      </v-col>

      <!-- Product Information -->
      <v-col md="5" cols="12" :class="$vuetify.breakpoint.smAndDown && `px-4`">
        <!-- Title -->
        <h1 class="text-h5 font-weight-medium" style="overflow-wrap: break-word">
          {{ product.title }}
        </h1>

        <!-- Detail (SEX, GENE, BIRTH, WEIGHT, DIET ) -->
        <div v-if="product.detail" class="text-subtitle-2">
          <!-- GENE CHIP -->
          <div class="my-2">
            <template v-for="{ title, path, s4 } in product.s3">
              <v-badge
                v-if="s4"
                :key="path"
                :content="s4.title"
                overlap
                offset-x="20"
                offset-y="16"
                class="prodview-badge"
              >
                <v-chip
                  class="mr-1 mt-1"
                  style="z-index: 0"
                  small
                  :ripple="false"
                  :to="`/animals/${product.s1.path}/${product.s2.path}/${path}`"
                  >{{ title }}</v-chip
                >
              </v-badge>
              <v-chip
                v-else
                class="mr-1 mt-1"
                small
                :ripple="false"
                :key="path"
                :to="`/animals/${product.s1.path}/${product.s2.path}/${path}`"
                >{{ title }}</v-chip
              >
            </template>
          </div>

          <v-row no-gutters class="align-center" v-if="product.detail.sex">
            {{ $langs.get("product.sex") }} :
            <v-icon small class="mx-1" :class="sex.color[product.detail.sex]">{{
              sex.icon[product.detail.sex]
            }}</v-icon>
            {{ sex.text[product.detail.sex] }}
          </v-row>
          <div v-if="product.detail.birth" class="mt-1">
            {{ $langs.get("product.birth") }} :
            {{ new Date(product.detail.birth).getFullYear() }}
          </div>
          <div v-if="product.detail.weight" class="mt-1">
            {{ $langs.get("product.weight") }} :
            {{ product.detail.weight }} g.
          </div>
          <div v-if="product.detail.diet" class="mt-1">
            {{ $langs.get("product.diet") }} : {{ product.detail.diet }}
          </div>
        </div>

        <!-- Store name -->
        <v-row
          no-gutters
          v-if="product.store._id"
          class="text-subtitle-2 mt-2 grey--text text--darken-1 align-center"
        >
          <v-icon left>mdi-store</v-icon>
          <router-link
            :to="{
              name: `StoreView`,
              params: {
                id: product.store._id,
                cacheData: product.store,
              },
            }"
            class="text-decoration-none"
            >{{ product.store.storeName }}</router-link
          >
        </v-row>
        <v-row
          v-if="product.store.address"
          no-gutters
          class="mt-1 text-subtitle-2 grey--text text--lighten-2 align-center"
        >
          <v-icon left small class="ml-1">mdi-map-marker</v-icon>
          {{ product.store.address.province }}
        </v-row>

        <!-- Price & Stock -->
        <v-row no-gutters class="mt-4" align="center">
          <span
            outlined
            class="text-h6 d-inline-block"
            :class="{
              'grey--text text-decoration-line-through': product.discountPrice,
            }"
            >{{ product.price.toLocaleString() }} THB</span
          >
          <span
            outlined
            class="ml-3 text-h5 font-weight-medium d-inline-block"
            v-if="product.discountPrice"
            >{{ product.discountPrice.toLocaleString() }} THB</span
          >
          <!-- DISCOUNT IN PERCENT (%) WILL BE HERE -->
          <!-- <v-card
            outlined
            class="py-2 px-4 ml-2 text-caption d-inline-block"
            :class="product.stock == 0 ? 'red--text text--lighten-1' : ''"
            >{{
              product.stock == 0
                ? `OUT OF STOCK`
                : `เหลือสินค้า ${product.stock} ชิ้น`
            }}
          </v-card> -->
        </v-row>

        <div class="text-caption">
          <v-card
            v-if="product.shippingPrice"
            outlined
            class="grey--text text--lighten-2 d-inline-block py-1 px-2 mr-2 mt-2"
            >{{ $langs.get("product.shippingPrice") }} +{{
              product.shippingPrice.toLocaleString()
            }}
            THB</v-card
          >
          <v-card
            outlined
            class="
              py-1
              px-2
              red
              darken-3
              white--text
              font-weight-medium
              d-inline-block
              elevation-2
            "
            style="left: 8px; transform: rotate(-10deg)"
            v-if="product.stock == 0"
            >SOLD OUT
          </v-card>
        </div>

        <!-- Contact Seller -->
        <v-btn
          color="primary"
          large
          :ripple="false"
          class="mt-4 px-8 py-6"
          :disabled="!product.store.contact"
          @click="contactStore"
          >{{ $langs.get("productView.contactStore") }}</v-btn
        >
      </v-col>
    </v-row>

    <v-divider class="bold-2 mt-8" v-if="product.title"></v-divider>

    <!-- Description -->
    <v-card class="pa-4 my-8" outlined tile v-if="product.description">
      <div class="text-h6 d-flex">
        {{ $langs.get("gen.description") }}
      </div>
      <div class="mt-2" v-html="product.description">
        <!-- Render with .innerHTML -->
      </div>
    </v-card>

    <!--  -------------------     END OF PAGE     ------------------  -->

    <!-- Seller Contact Popup -->
    <v-dialog v-model="dialog" width="450" eager>
      <v-card>
        <v-card-title class="headline darken-1"> Contact </v-card-title>

        <v-card-text class="py-3">
          <template v-for="(v, k) in product.store.contact">
            <div v-if="v" :key="k" class="d-flex py-2 align-center" no-gutters>
              <v-icon
                class="mr-2"
                :style="k == 'line' ? 'margin-left: 2px' : ''"
                >{{ contact.icon[k] }}</v-icon
              >
              <a
                v-if="isLink(k, v)"
                class="text-truncate link d-inline-block"
                :href="genLinkHref(k, v)"
                :target="k != 'tel' && k != 'email' && '_blank'"
                >{{ v.replace("https://", "").replace("http://", "") }}</a
              >
              <span v-else>{{ v }}</span>
            </div>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text :ripple="false" @click="dialog = false">
            {{  $langs.get("gen.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ImagesPreview from "@/components/base/ImagesPreview";
import user from "../services/user";
// import { productImageUrl, getTitle } from "@/services/constants";
// import api from "@/services/api";

import { productImageUrl } from "../services/constants";
import api from "../services/api";

import Announcement from "@/components/base/Announcement";

export default {
  props: ["cacheData"],
  components: {
    ImagesPreview,
    Announcement,
  },
  data() {
    return {
      product: {
        title: "",
        description: "",
        price: "",
        discountPrice: 0,
        stock: 1,
        images: [], // ["https://picsum.photos/id/237/400/300"],
        storeID: "",
        store: { storeName: "" },
        _id: "-1",
      },
      breadcrumb: [],
      dialog: false,
      contact: {
        title: {
          tel: "Telephone",
          fb: "Facebook",
          web: "Website",
          line: "Line",
          email: "Email",
        },
        icon: {
          tel: "mdi-cellphone",
          fb: "mdi-facebook",
          web: "mdi-web",
          line: "fab fa-line",
          email: "mdi-email",
        },
      },
      didClickContact: false,
      sex: {
        text: {
          m: "Male",
          w: "Female",
          o: "Unknown",
        },
        icon: {
          m: "mdi-gender-male",
          w: "mdi-gender-female",
          o: "",
        },
        color: {
          m: "blue--text",
          w: "pink--text",
        },
      },
    };
  },
  methods: {
    async fetchProductInfo() {
      // let type = this.$route.path.slice(1).split("/")[0];
      let id = this.$route.params.id;
      let admin = this.$route.query.admin;
      let store = this.$route.query.store;
      let pathApi = `id/${id}`;

      // cast data to product
      var { data: product, status } = await api.getProducts(pathApi, {
        admin,
        store,
      });
      if (status != undefined && status != 200)
        return this.$router.replace("/notfound");

      // Parse images
      product.images = product.images.map((image) => productImageUrl(image));
      if (product.description)
        product.description = product.description
          .replace(/</g, "&#60;")
          .replace(/\n/g, "<br>")
          .replace(/\s\s/g, "&ensp;");
      this.product = { ...product };
      this.updatePathOverview({ s1: product.s1, s2: product.s2 });

      if (typeof gtag !== 'undefined') {
        gtag("event", "view_item", {
          currency: "THB",
          value: product.price,
          items: [{
            item_id: product._id,
            item_name: product.title,
            price: product.price
          }]
        });
      }
    },
    async updatePathOverview({ s1, s2 }) {
      this.breadcrumb = [
        { text: this.$langs.get("gen.home"), to: "/" },
      ];
      this.breadcrumb.push({
        text: this.$langs.get(`gen.${this.product.type}`),
        to: `/${this.product.type}`,
        exact: true,
      });
      this.breadcrumb.push({
        text: this.$langs.getFunc(`product.subType`)(s1),
        to: `/${this.product.type}/${s1.path}`,
      });
      if (s2)
        this.breadcrumb.push({
          text: s2.title,
          to: `/${this.product.type}/${s1.path}/${s2.path}`,
        });
      this.breadcrumb.push({
        text: this.product.title,
        to: this.$route.path,
      });
    },
    async contactStore() {
      this.dialog = true;
      let { ts, h, _id } = this.product;
      if (this.didClickContact) return;
      this.didClickContact = true;
      user.clickContactStore({ ts, h, _id });

      if (typeof gtag !== 'undefined') {
        gtag("event", "add_to_cart", {
          currency: "THB",
          value: this.product.price,
          items: [{
            item_id: this.product._id,
            item_name: this.product.title,
            price: this.product.price
          }]
        });
      }
    },
    addItem() {
      this.$store.dispatch({
        type: "EditItem",
        cartItem: {
          title: this.product.title,
          price: this.product.price,
          type: this.product.type,
          id: this.product._id,
        },
      });
    },
    async reportProduct() {
      let message = prompt("รายละเอียด");
      if (!message) return;
      const { success, msg } = await user.reportProduct(
        this.product._id,
        message
      );
      if (!success) return api.makeAlert(msg, "error");
      api.makeAlert("Your report is sent.", "primary");
    },
    isLink(key, web) {
      if (key == "line" && !web.includes("line.me")) return false;
      if (key == "tel" || key == "email") return true;
      return /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi.test(
        web
      );
    },
    genLinkHref(key, v) {
      if (key == "tel") return `tel:${v.replace(/-/g, "")}`;
      if (key == "email") return `mailto:${v}`;
      return `${v.indexOf("http") == -1 ? "http://" : ""}${v}`;
    },
    shareLink() {
      navigator.share({
        title: this.product.title,
        url: this.$route.path,
      });
    },
  },
  computed: {
    addAble() {
      if (this.product._id == -1) return false;
      let cartItems = this.$store.getters["cartItems"];
      if (!cartItems[this.product._id]) return true;
      if (
        cartItems[this.product._id].type != "animals" &&
        cartItems[this.product._id].quantity < this.product.stock
      ) {
        return true;
      }
      return false;
    },
    showReportBtn() {
      return !!user.restore().userInfo;
    },
  },
  mounted() {
    if (this.cacheData) {
      this.product = { ...this.product, ...this.cacheData };
      this.updatePathOverview({ s1: this.product.s1, s2: this.product.s2 });
    }
    this.fetchProductInfo();
  },
  metaInfo() {
    return {
      title: this.product.title || "",
      titleTemplate: "%s · ReptTown",
      meta: [
        {
          vmid: "description",
          name: "description",
          content: this.product.description,
        },
      ],
    };
  },
};
</script>

<style lang="sass">
.prodview-badge
  .v-badge__badge
    opacity: .35
    z-index: 1
    pointer-events: none
  &:hover .v-badge__badge
    opacity: 1
</style>